<template>

  <div>

    <coupon-list-add-new
      :is-add-new-coupon-sidebar-active.sync="isAddNewCouponSidebarActive"
      @refetch-data="fetchData(true)"
    />

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >

      <div class="m-2">

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          />
          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Search..."
              />
              <b-button
                variant="primary"
                @click="isAddNewCouponSidebarActive = true"
              >
                <span class="text-nowrap">{{ $t('create') }}</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

      </div>

      <b-table
        ref="refUserListTable"
        class="position-relative"
        :items="couponList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :filter="searchQuery"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no coupons created')"
        :sort-desc.sync="isSortDirDesc"
      >

        <!-- Column: Offer Name -->
        <template #cell(offerName)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.offerName }}</span>
          </div>
        </template>

        <!-- Column: Discount Amount -->
        <template #cell(discountAmount)="data">
          <div class="text-nowrap text-center">
            <span class="align-text-top"><span v-if="data.item.offerType === 'flat'">-</span>{{ data.item.discountAmount }}<span v-if="data.item.offerType === 'percentage'">%</span></span>
          </div>
        </template>

        <!-- Column: Maximum Total Usage -->
        <template #cell(maximumTotalUsage)="data">
          <div class="text-nowrap text-center">
            <span class="align-text-top"><span v-if="data.item.maximumTotalUsage === 0">ꝏ</span><span v-else>{{ data.item.maximumTotalUsage }}</span></span>
          </div>
        </template>

        <template #cell(startDateTime)="data">
          <div class="text-nowrap">
            <small
              v-if="data.item.startDateTime"
              class="align-text-top d-block"
            >
              {{ data.item.startDateTime.toDate() | moment("YYYY/MM/DD hh:mm") }}
            </small>
            <small
              v-if="data.item.endDateTime"
              class="align-text-bottom d-block"
            >
              {{ data.item.endDateTime.toDate() | moment("YYYY/MM/DD hh:mm") }}
            </small>
          </div>
        </template>

        <!-- Column: Description -->
        <template #cell(description)="data">
          <div class="">
            <span class="">
              {{ data.item.description }}
            </span>
          </div>
        </template>

        <!-- Column: Campaigns -->
        <template #cell(campaigns)="data">
          <div class="text-nowrap text-center">
            <span class="align-text-top"><span v-if="data.item.campaigns[0] === '*'">{{ $t('all') }}</span><span v-else>{{ data.item.campaigns.length }}</span></span>
          </div>
        </template>

        <!-- Column: Number Usage Per Customer -->
        <template #cell(perCustomer)="data">
          <div class="text-nowrap text-center">
            <span class="align-text-top"><span v-if="data.item.perCustomer === 0">{{ $t('none') }}</span><span v-else>{{ data.item.campaigns.length }}</span></span>
          </div>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge
            pill
            :variant="`light-${statusVariant(data.item.status)}`"
            class="text-capitalize text-center"
          >
            {{ $t(data.item.status) }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              @click="editCoupon(data.item)"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('edit') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="data.item.status === 'active'"
              @click="updateStatus({ cpid: data.item.cpid, status: 'inactive' })"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('inactive') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="data.item.status === 'inactive'"
              @click="updateStatus({ cpid: data.item.cpid, status: 'active' })"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('active') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="data.item.status === 'inactive'"
              @click="confirmDelete({ cpid: data.item.cpid })"
            >
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">{{ $t('delete') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          />
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >

            <b-pagination
              v-model="currentPage"
              :total-rows="totalUsers"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>

          </b-col>

        </b-row>
      </div>
    </b-card>

    <b-modal
      id="modal-edit"
      cancel-variant="outline-secondary"
      :ok-title="$t('update')"
      :ok-disabled="!readyToSubmit"
      :cancel-title="$t('cancel')"
      centered
      :title="$t('edit coupon')"
      @ok="updateCoupon"
    >
      <CouponInput
        :coupon="couponData"
        :user-id="user.uid"
        :campaign-list="userCampaigns"
        @ready="isReady => readyToSubmit = isReady"
        @update="data => updateData = data"
      />
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable,
  BBadge, BDropdown, BDropdownItem, BPagination, BModal,
} from 'bootstrap-vue'
import store from '@/store'
import { mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import CouponModule from '@/store/modules/couponModule'
import CouponListAddNew from './CouponAddNew.vue'
import CouponInput from './CouponInput.vue'

export default {
  components: {
    CouponListAddNew,
    CouponInput,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BModal,
  },
  data() {
    return {
      isAddNewCouponSidebarActive: false,
      refUserListTable: null,
      couponList: [],
      tableColumns: [
        { key: 'offerName', label: this.$t('offer name'), thClass: 'text-nowrap' },
        { key: 'discountAmount', label: this.$t('amount percentage'), thClass: 'text-nowrap' },
        { key: 'maximumTotalUsage', label: this.$t('maximum total usage'), thClass: 'text-nowrap' },
        {
          key: 'startDateTime', label: this.$t('start end date time'), sortable: true, thClass: 'text-nowrap',
        },
        { key: 'code', label: this.$t('coupon code'), thClass: 'text-nowrap' },
        {
          key: 'description', label: this.$t('description'), thClass: 'text-nowrap', thStyle: { minWidth: '250px' },
        },
        { key: 'campaigns', label: this.$t('campaigns'), thClass: 'text-nowrap' },
        { key: 'perCustomer', label: this.$t('limit usage'), thClass: 'text-nowrap' },
        { key: 'status', label: this.$t('status'), thClass: 'text-nowrap text-center' },
        { key: 'actions', label: this.$t('actions'), thClass: 'text-nowrap text-center' },
      ],
      perPage: 10,
      totalUsers: 0,
      currentPage: 1,
      perPageOptions: [10, 25, 50, 100],
      searchQuery: '',
      sortBy: 'createAt',
      isSortDirDesc: true,

      dataMeta: {
        from: 10,
        to: 20,
        of: 1,
      },
      couponData: null,
      updateData: null,
      readyToSubmit: false,
    }
  },
  computed: {
    ...mapGetters(['userCampaigns', 'user']),
  },
  mounted() {
    this.$store.registerModule('coupon', CouponModule)
    this.fetchData()
  },
  beforeDestroy() {
    this.$store.unregisterModule('coupon')
  },
  methods: {
    updateCoupon() {
      const cpid = this.couponData.code
      const params = this.updateData

      this.$store.dispatch('updateCouponData', { uid: this.user.uid, cpid, params }).then(() => {
        const objIndex = this.couponList.findIndex((obj => obj.code === cpid))
        const arr = { ...this.couponList[objIndex], ...params }
        Vue.set(this.couponList, objIndex, arr)
      })
    },
    editCoupon(data) {
      this.couponData = {
        cpid: data.cpid,
        startDateTime: (data.startDateTime) ? this.$moment(data.startDateTime.toDate()).format('YYYY-MM-DD HH:mm') : null,
        endDateTime: (data.endDateTime) ? this.$moment(data.endDateTime.toDate()).format('YYYY-MM-DD HH:mm') : null,
        offerName: data.offerName,
        offerType: data.offerType,
        maximumTotalUsage: data.maximumTotalUsage,
        perCustomer: data.perCustomer,
        discountAmount: data.discountAmount,
        codes: data.codes,
        code: data.code,
        campaigns: data.campaigns,
        description: data.description,
      }

      this.$bvModal.show('modal-edit')
    },
    fetchData(refetch) {
      store.dispatch('fetchCouponList', { uid: this.user.uid }).then(list => {
        this.couponList = (list) || []
        if (refetch) this.$forceUpdate()
      })
    },
    statusVariant: (status => {
      if (status === 'active') return 'success'
      if (status === 'inactive') return 'secondary'
      if (status === 'suspended') return 'danger'
      return 'primary'
    }),
    async updateStatus({ status, cpid }) {
      await store.dispatch('updateCouponStatus', { status, uid: this.user.uid, cpid })
      const objIndex = this.couponList.findIndex((obj => obj.code === cpid))
      this.couponList[objIndex].status = status
    },
    confirmDelete({ cpid }) {
      this.$swal({
        title: '確定要刪除嗎?',
        text: '刪除以後便無法恢復！',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定, 刪除！',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          store.dispatch('updateCouponStatus', { status: 'deleted', uid: this.user.uid, cpid })
            .then(() => {
              const removeIndex = this.couponList.map(item => item.code).indexOf(cpid)
              this.couponList.splice(removeIndex, 1)

              this.$toast({
                component: ToastificationContent,
                props: {
                  title: '優惠卷已成功刪除',
                  icon: 'CheckCircleIcon',
                  variant: 'success',
                },
              })
            })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
